import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  Upload, 
  Download, 
  ShoppingCart, 
  RotateCcw,
  Palette,
  Type,
  Image as ImageIcon,
  Smartphone,
  CreditCard,
  Mail,
  Phone,
  Briefcase,
  User,
  Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CardPreview from '../components/CardPreview';
import axios from 'axios';
import { toast } from 'sonner';
import { trackLead } from '../utils/fbpixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomizeProduct = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [customization, setCustomization] = useState({
    // Card Style
    template: 'modern',
    cardColor: '#1a1a1a',
    accentColor: '#3b82f6',
    textColor: '#ffffff',
    
    // Personal Info
    name: 'John Smith',
    title: 'Senior Marketing Director',
    company: 'Tech Innovations Inc.',
    phone: '+1 (555) 123-4567',
    email: 'john.smith@techinnovations.com',
    website: 'www.techinnovations.com',
    
    // Branding
    logo: null,
    logoPreview: null,
    
    // Typography
    font: 'inter'
  });

  const [selectedProduct, setSelectedProduct] = useState('card');

  const handleInputChange = (field, value) => {
    setCustomization(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomization(prev => ({
          ...prev,
          logo: file,
          logoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReset = () => {
    setCustomization({
      template: 'modern',
      cardColor: '#1a1a1a',
      accentColor: '#3b82f6',
      textColor: '#ffffff',
      name: 'John Smith',
      title: 'Senior Marketing Director',
      company: 'Tech Innovations Inc.',
      phone: '+1 (555) 123-4567',
      email: 'john.smith@techinnovations.com',
      website: 'www.techinnovations.com',
      logo: null,
      logoPreview: null,
      font: 'inter'
    });
  };

  const handleAddToCart = async () => {
    try {
      setSaving(true);
      
      // Save design to backend
      const designData = {
        template: customization.template,
        card_color: customization.cardColor,
        accent_color: customization.accentColor,
        text_color: customization.textColor,
        name: customization.name,
        title: customization.title,
        company: customization.company,
        phone: customization.phone,
        email: customization.email,
        website: customization.website,
        logo_url: customization.logoPreview,
        font: customization.font,
        product_type: selectedProduct
      };
      
      const designResponse = await axios.post(`${API}/designs`, designData);
      const savedDesign = designResponse.data;
      
      toast.success('Design saved successfully!');
      
      // Add to cart
      const cartData = {
        design_id: savedDesign.id,
        product_type: selectedProduct,
        quantity: 1
      };
      
      await axios.post(`${API}/cart`, cartData);
      
      toast.success('Added to cart!');
      
      // Navigate to cart page (we'll create this next)
      setTimeout(() => {
        navigate('/cart');
      }, 500);
      
    } catch (error) {
      console.error('Error saving design:', error);
      toast.error('Failed to add to cart. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const templates = [
    { value: 'modern', label: 'Modern Minimal', description: 'Clean and professional' },
    { value: 'corporate', label: 'Corporate Bold', description: 'Traditional business style' },
    { value: 'creative', label: 'Creative Edge', description: 'Stand out design' },
    { value: 'elegant', label: 'Elegant Classic', description: 'Timeless sophistication' }
  ];

  const fonts = [
    { value: 'inter', label: 'Inter' },
    { value: 'poppins', label: 'Poppins' },
    { value: 'roboto', label: 'Roboto' },
    { value: 'playfair', label: 'Playfair Display' },
    { value: 'montserrat', label: 'Montserrat' },
    { value: 'opensans', label: 'Open Sans' },
    { value: 'lato', label: 'Lato' },
    { value: 'raleway', label: 'Raleway' },
    { value: 'oswald', label: 'Oswald' },
    { value: 'merriweather', label: 'Merriweather' },
    { value: 'nunito', label: 'Nunito' },
    { value: 'ubuntu', label: 'Ubuntu' },
    { value: 'worksans', label: 'Work Sans' },
    { value: 'sourcesans', label: 'Source Sans Pro' },
    { value: 'crimsontext', label: 'Crimson Text' },
    { value: 'libreFranklin', label: 'Libre Franklin' },
    { value: 'spacegrotesk', label: 'Space Grotesk' },
    { value: 'bebasneue', label: 'Bebas Neue' },
    { value: 'cormorant', label: 'Cormorant Garamond' }
  ];

  const colorPresets = [
    { name: 'Tech Blue', card: '#1a1a1a', accent: '#3b82f6' },
    { name: 'Ocean Teal', card: '#0f172a', accent: '#14b8a6' },
    { name: 'Forest Green', card: '#1a1a1a', accent: '#10b981' },
    { name: 'Sunset Orange', card: '#1c1917', accent: '#f97316' },
    { name: 'Royal Purple', card: '#1e1b4b', accent: '#8b5cf6' },
    { name: 'Crimson Red', card: '#1a1a1a', accent: '#ef4444' },
    { name: 'Gold Luxury', card: '#18181b', accent: '#eab308' },
    { name: 'Rose Pink', card: '#1c1917', accent: '#f43f5e' }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-black/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white/5"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Home
            </Button>
            <h1 className="text-2xl font-bold text-white">NFC Card Designer</h1>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleReset}
                className="border-zinc-700 text-white hover:bg-white/5"
              >
                <RotateCcw className="mr-2 w-4 h-4" />
                Reset
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Side - Preview */}
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">Live Preview</h2>
              <p className="text-gray-400">See your design in real-time</p>
            </div>

            {/* Product Type Selector */}
            <div className="flex gap-4">
              {[
                { id: 'card', icon: CreditCard, label: 'Business Card' },
                { id: 'keychain', icon: Smartphone, label: 'Keychain' }
              ].map((product) => (
                <button
                  key={product.id}
                  onClick={() => setSelectedProduct(product.id)}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    selectedProduct === product.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-zinc-800 bg-zinc-900 hover:border-zinc-700'
                  }`}
                >
                  <product.icon className={`w-6 h-6 mx-auto mb-2 ${
                    selectedProduct === product.id ? 'text-blue-500' : 'text-gray-400'
                  }`} />
                  <div className={`text-sm font-medium ${
                    selectedProduct === product.id ? 'text-white' : 'text-gray-400'
                  }`}>
                    {product.label}
                  </div>
                </button>
              ))}
            </div>

            {/* Card Preview Component */}
            <CardPreview 
              customization={customization} 
              productType={selectedProduct}
            />

            {/* Preview Info */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-4">How It Works</h3>
                <ul className="space-y-3 text-sm text-gray-400">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-blue-500 text-xs font-bold">1</span>
                    </div>
                    <span>Customize your design using the controls on the right</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-blue-500 text-xs font-bold">2</span>
                    </div>
                    <span>See changes instantly in the live preview above</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-blue-500 text-xs font-bold">3</span>
                    </div>
                    <span>Submit your design to get a quote</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Customization Controls */}
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">Customize Your Card</h2>
              <p className="text-gray-400">Design your perfect NFC business card</p>
            </div>

            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-3 bg-zinc-900">
                <TabsTrigger value="info" className="data-[state=active]:bg-blue-500">
                  <User className="w-4 h-4 mr-2" />
                  Info
                </TabsTrigger>
                <TabsTrigger value="design" className="data-[state=active]:bg-blue-500">
                  <Palette className="w-4 h-4 mr-2" />
                  Design
                </TabsTrigger>
                <TabsTrigger value="branding" className="data-[state=active]:bg-blue-500">
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Branding
                </TabsTrigger>
              </TabsList>

              {/* Personal Info Tab */}
              <TabsContent value="info" className="space-y-4 mt-6">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardContent className="p-6 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white flex items-center gap-2">
                        <User className="w-4 h-4 text-blue-500" />
                        Full Name
                      </Label>
                      <Input
                        id="name"
                        value={customization.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="title" className="text-white flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-blue-500" />
                        Job Title
                      </Label>
                      <Input
                        id="title"
                        value={customization.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="Your job title"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-white flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-blue-500" />
                        Company
                      </Label>
                      <Input
                        id="company"
                        value={customization.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="Company name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white flex items-center gap-2">
                        <Phone className="w-4 h-4 text-blue-500" />
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        value={customization.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-500" />
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={customization.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="your.email@company.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website" className="text-white flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-blue-500" />
                        Website
                      </Label>
                      <Input
                        id="website"
                        value={customization.website}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="www.yourwebsite.com"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Design Tab */}
              <TabsContent value="design" className="space-y-4 mt-6">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardContent className="p-6 space-y-6">
                    {/* Template Selection */}
                    <div className="space-y-3">
                      <Label className="text-white font-semibold">Card Template</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {templates.map((template) => (
                          <button
                            key={template.value}
                            onClick={() => handleInputChange('template', template.value)}
                            className={`p-4 rounded-lg border-2 text-left transition-all ${
                              customization.template === template.value
                                ? 'border-blue-500 bg-blue-500/10'
                                : 'border-zinc-800 bg-zinc-800 hover:border-zinc-700'
                            }`}
                          >
                            <div className={`font-medium text-sm mb-1 ${
                              customization.template === template.value ? 'text-white' : 'text-gray-400'
                            }`}>
                              {template.label}
                            </div>
                            <div className="text-xs text-gray-500">{template.description}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Color Presets */}
                    <div className="space-y-3">
                      <Label className="text-white font-semibold">Color Presets</Label>
                      <div className="grid grid-cols-4 gap-3">
                        {colorPresets.map((preset, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              handleInputChange('cardColor', preset.card);
                              handleInputChange('accentColor', preset.accent);
                            }}
                            className="group relative"
                            title={preset.name}
                          >
                            <div className="aspect-square rounded-lg overflow-hidden border-2 border-zinc-700 hover:border-blue-500 transition-colors">
                              <div className="h-full" style={{ backgroundColor: preset.card }}>
                                <div className="h-1/3" style={{ backgroundColor: preset.accent }}></div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1 text-center group-hover:text-white transition-colors">
                              {preset.name.split(' ')[0]}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Colors */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="cardColor" className="text-white">Card Color</Label>
                        <div className="flex gap-2">
                          <Input
                            id="cardColor"
                            type="color"
                            value={customization.cardColor}
                            onChange={(e) => handleInputChange('cardColor', e.target.value)}
                            className="w-16 h-10 p-1 bg-zinc-800 border-zinc-700 cursor-pointer"
                          />
                          <Input
                            value={customization.cardColor}
                            onChange={(e) => handleInputChange('cardColor', e.target.value)}
                            className="flex-1 bg-zinc-800 border-zinc-700 text-white"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="accentColor" className="text-white">Accent Color</Label>
                        <div className="flex gap-2">
                          <Input
                            id="accentColor"
                            type="color"
                            value={customization.accentColor}
                            onChange={(e) => handleInputChange('accentColor', e.target.value)}
                            className="w-16 h-10 p-1 bg-zinc-800 border-zinc-700 cursor-pointer"
                          />
                          <Input
                            value={customization.accentColor}
                            onChange={(e) => handleInputChange('accentColor', e.target.value)}
                            className="flex-1 bg-zinc-800 border-zinc-700 text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Font Selection */}
                    <div className="space-y-2">
                      <Label htmlFor="font" className="text-white flex items-center gap-2">
                        <Type className="w-4 h-4 text-blue-500" />
                        Font Family
                      </Label>
                      <Select value={customization.font} onValueChange={(value) => handleInputChange('font', value)}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {fonts.map((font) => (
                            <SelectItem key={font.value} value={font.value} className="text-white hover:bg-zinc-700">
                              {font.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Branding Tab */}
              <TabsContent value="branding" className="space-y-4 mt-6">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardContent className="p-6 space-y-4">
                    <div className="space-y-3">
                      <Label className="text-white font-semibold flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-blue-500" />
                        Company Logo
                      </Label>
                      
                      {customization.logoPreview ? (
                        <div className="space-y-4">
                          <div className="relative aspect-video bg-zinc-800 rounded-lg overflow-hidden border-2 border-zinc-700">
                            <img 
                              src={customization.logoPreview} 
                              alt="Logo preview"
                              className="w-full h-full object-contain p-4"
                            />
                          </div>
                          <Button
                            variant="outline"
                            onClick={() => handleInputChange('logoPreview', null)}
                            className="w-full border-zinc-700 text-white hover:bg-white/5"
                          >
                            Remove Logo
                          </Button>
                        </div>
                      ) : (
                        <label className="block">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                          <div className="border-2 border-dashed border-zinc-700 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors">
                            <Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                            <p className="text-white font-medium mb-2">Upload Your Logo</p>
                            <p className="text-sm text-gray-500">PNG, JPG, SVG up to 5MB</p>
                          </div>
                        </label>
                      )}
                    </div>

                    <div className="pt-4 border-t border-zinc-800">
                      <h4 className="text-white font-semibold mb-3">Logo Guidelines</h4>
                      <ul className="space-y-2 text-sm text-gray-400">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-500 mt-1">•</span>
                          <span>Use a transparent PNG for best results</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-500 mt-1">•</span>
                          <span>Square or horizontal logos work best</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-500 mt-1">•</span>
                          <span>Minimum 500x500px recommended</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-500 mt-1">•</span>
                          <span>High contrast logos are easier to read</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            
            {/* Submit Design Button - Prominent placement */}
            <Card className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 border-purple-500/30 mt-8">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">Love Your Design?</h3>
                    <p className="text-gray-300 text-sm mb-4">
                      Submit this custom design and get a personalized quote for your NFC cards within 24-48 hours. Perfect for bulk orders, corporate gifts, or events.
                    </p>
                    <Button
                      onClick={() => setShowSubmitModal(true)}
                      size="lg"
                      className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-semibold"
                    >
                      <Mail className="mr-2 w-5 h-5" />
                      Submit This Design for Quote
                    </Button>
                  </div>
                  <div className="hidden md:block">
                    <div className="text-6xl">🎨</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Submit Design Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-gray-800 max-w-md w-full">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-white mb-4">Request Custom Quote</h2>
              <p className="text-gray-400 mb-6">
                Submit your custom design and we'll send you a personalized quote within 24-48 hours.
              </p>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                setSubmitting(true);
                
                try {
                  let logoUrl = null;
                  
                  // Upload logo if one exists
                  if (customization.logo && customization.logo instanceof File) {
                    const formData = new FormData();
                    formData.append('file', customization.logo);
                    
                    const uploadResponse = await axios.post(
                      `${BACKEND_URL}/api/bulk-orders/upload-logo`,
                      formData,
                      { headers: { 'Content-Type': 'multipart/form-data' } }
                    );
                    
                    logoUrl = uploadResponse.data.file_url;
                  }
                  
                  // Prepare design data with uploaded logo URL
                  const designDataToSubmit = {
                    ...customization,
                    logo: null, // Remove file object
                    logoUrl: logoUrl || customization.logoPreview, // Use uploaded URL or preview
                    logoPreview: null // Remove preview data URL
                  };
                  
                  const submitRes = await axios.post(`${BACKEND_URL}/api/custom-designs/`, {
                    customer_name: customerInfo.name,
                    customer_email: customerInfo.email,
                    customer_phone: customerInfo.phone,
                    design_data: designDataToSubmit,
                    notes: customerInfo.notes
                  });

                  // Meta Pixel — Lead (deduped via server-generated fb_event_id)
                  trackLead({
                    contentName: 'Custom Design Request',
                    eventID: submitRes.data?.fb_event_id,
                  });

                  toast.success('Design submitted successfully! We\'ll contact you soon.');
                  setShowSubmitModal(false);
                  setCustomerInfo({ name: '', email: '', phone: '', notes: '' });
                } catch (error) {
                  toast.error('Failed to submit design. Please try again.');
                  console.error('Submit error:', error);
                } finally {
                  setSubmitting(false);
                }
              }} className="space-y-4">
                <div>
                  <Label className="text-gray-300">Your Name *</Label>
                  <Input
                    type="text"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                    required
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Email *</Label>
                  <Input
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                    required
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Phone Number *</Label>
                  <Input
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                    required
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Additional Notes (Optional)</Label>
                  <textarea
                    value={customerInfo.notes}
                    onChange={(e) => setCustomerInfo({...customerInfo, notes: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white"
                    placeholder="Quantity needed, timeline, special requirements..."
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Design'
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowSubmitModal(false)}
                    variant="outline"
                    className="flex-1 border-2 border-gray-600 hover:border-gray-500 bg-gray-800 hover:bg-gray-700 text-white font-medium"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CustomizeProduct;
